import {Box, Flex, Text, themeContext} from 'exsportia-components'
import React, {FC, useCallback, useContext, useState} from 'react'

import {BookingForm, BookingFormData, FormResponse} from '../../api/forms/types'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {getQuestionId, QuestionField} from './question-field'

type Props = {
  form: BookingForm
  onComplete: (formData: BookingFormData) => void
  isLoading?: boolean
  initialData?: BookingFormData
}

export const BookingFormModalContent: FC<Props> = ({
  form,
  onComplete,
  isLoading,
  initialData,
}) => {
  const {l} = useLocale()
  const {theme} = useContext(themeContext)
  const [responses, setResponses] = useState<Record<string, any>>(() => {
    const init: Record<string, any> = {}
    if (initialData?.responses) {
      initialData.responses.forEach(r => {
        init[r.questionId] = r.value
      })
    }
    return init
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = useCallback((questionId: string, value: any) => {
    setResponses((prev) => ({...prev, [questionId]: value}))
    setErrors((prev) => {
      const next = {...prev}
      delete next[questionId]
      return next
    })
  }, [])

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {}
    form.questions?.forEach((q) => {
      const qId = getQuestionId(q)
      const val = responses[qId]

      if (q.required) {
        const isEmpty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)
        if (isEmpty) {
          newErrors[qId] = 'This field is required'
        }
      }

      if (q.type === 'number' && val !== undefined && val !== '') {
        const num = Number(val)
        const minVal = q.config?.min !== undefined && q.config?.min !== '' ? Number(q.config.min) : undefined
        const maxVal = q.config?.max !== undefined && q.config?.max !== '' ? Number(q.config.max) : undefined

        if (minVal !== undefined && num < minVal) {
          newErrors[qId] = `Value must be at least ${minVal}`
        }
        if (maxVal !== undefined && num > maxVal) {
          newErrors[qId] = `Value must be no more than ${maxVal}`
        }
      }

      if (q.type === 'checkboxes' && Array.isArray(val) && val.length > 0) {
        if (q.config?.min !== undefined && val.length < Number(q.config.min)) {
          newErrors[qId] = `Please select at least ${q.config.min} options`
        }
        if (q.config?.max !== undefined && val.length > Number(q.config.max)) {
          newErrors[qId] = `Please select no more than ${q.config.max} options`
        }
      }
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [form, responses])

  const handleSave = useCallback(() => {
    if (validate()) {
      setIsSubmitting(true)
      const formattedResponses: FormResponse[] = form.questions?.map((q) => {
        const qId = getQuestionId(q)
        return {
          questionId: qId,
          value: responses[qId] ?? (q.type === 'checkboxes' ? [] : ''),
        }
      }) || []

      onComplete({
        formId: form.formId,
        responses: formattedResponses,
      })
    }
  }, [validate, form, responses, onComplete])

  return (
    <Box style={{paddingBottom: '24px', maxWidth: '100%', width: '100%'}}>
      {form.description && (
        <Box mb='24px'>
          <Text settings={{color: theme.colors.gray, fontSize: '14px', display: 'block'}}>
            {form.description}
          </Text>
        </Box>
      )}

      <Flex flexDirection='column' gap='20px'>
        {form.questions?.map((question, index) => {
          const qId = getQuestionId(question)
          return (
            <QuestionField
              key={qId}
              question={question}
              value={responses[qId]}
              error={errors[qId]}
              onChange={handleChange}
              theme={theme}
              formId={form.formId}
            />
          )
        })}
      </Flex>

      <Box mt='32px'>
        <Button
          onClick={handleSave}
          disabled={isLoading || isSubmitting}
          isLoading={isLoading || isSubmitting}
          settings={{width: '100%', height: '52px', fontSize: '16px', fontWeight: 'bold'}}
        >
          {l('continue')}
        </Button>
      </Box>
    </Box>
  )
}
